<template>
  <div class="franquicias-view">
    <!-- Hero Header -->
    <FranquiciaHero
      v-if="!loading && datosFranquicia"
      :imagen-fondo="datosFranquicia.ImagenHeader"
      :titulo="datosFranquicia.TituloHeader || datosFranquicia.NombreFranquicia || 'SUCURSALES'"
      :subtitulo="datosFranquicia.SubtituloHeader"
      :color-primario="datosFranquicia.PrimaryColor"
      :color-secundario="datosFranquicia.SecondaryColor"
    />

    <!-- Alert de ubicación -->
    <v-alert
      v-if="accesoDenegadoUsuario"
      type="warning"
      outlined
      dismissible
      class="mx-4 mt-4"
    >
      Has denegado el acceso a tu ubicación. No podremos mostrarte las sucursales más cercanas.
    </v-alert>

    <!-- Skeleton Loaders -->
    <v-container v-if="loading">
      <v-row v-if="$isMobile()">
        <v-col cols="12">
          <v-skeleton-loader type="list-item-three-line" v-for="i in 3" :key="`skeleton_${i}`"></v-skeleton-loader>
        </v-col>
      </v-row>
      <v-row v-else>
        <v-col cols="12" md="4" v-for="i in 6" :key="`skeleton_${i}`">
          <v-skeleton-loader type="card"></v-skeleton-loader>
        </v-col>
      </v-row>
    </v-container>

    <!-- Contenido Principal -->
    <div v-if="!loading && (sucursalesFiltradas.length > 0 || sucursales.length > 0)">
      <!-- Botón volver (mobile) -->
      <go-back
        v-if="$isMobile() && showGoBack"
        :onlyAndroid="true"
        :forceRoute="true"
        :routeName="previousPage"
        label="Volver a tiendas"
        class="mx-2 mt-2"
      />

      <!-- Buscador de Sucursales -->
      <BuscadorSucursales
        @filtro-change="aplicarFiltros"
      />

      <!-- Sucursal Más Cercana (destacada) -->
      <v-container v-if="sucursalMasCercana && !mostrarSoloLista">
        <div class="seccion-destacada">
          <h2 class="seccion-titulo">
            <v-icon left color="primary">mdi-map-marker-distance</v-icon>
            Tu sucursal más cercana
          </h2>
          <SucursalCard
            :sucursal="sucursalMasCercana"
            :es-mas-cercana="true"
            class="destacada-card"
          />
        </div>

        <!-- Separador -->
        <v-divider class="my-6"></v-divider>

        <!-- Título de búsqueda -->
        <div class="seccion-busqueda">
          <h2 class="seccion-titulo">
            <v-icon left color="primary">mdi-map-search</v-icon>
            Buscar sucursal
          </h2>
        </div>
      </v-container>

      <!-- Lista de Sucursales -->
      <v-container>
        <v-row>
          <v-col
            :cols="12"
            :md="4"
            v-for="(sucursal, index) in sucursalesParaMostrar"
            :key="`sucursal_${index}_${sucursal.Code || index}`"
          >
            <SucursalCard
              :sucursal="sucursal"
              :es-mas-cercana="false"
            />
          </v-col>
        </v-row>

        <!-- Mensaje si no hay resultados -->
        <v-row v-if="sucursalesParaMostrar.length === 0">
          <v-col cols="12" class="text-center py-8">
            <v-icon large color="grey lighten-1" class="mb-4">mdi-map-marker-off</v-icon>
            <p class="text-h6 grey--text">No se encontraron sucursales con los filtros seleccionados</p>
            <v-btn
              outlined
              color="primary"
              @click="limpiarFiltros"
              class="mt-4"
            >
              Limpiar filtros
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </div>

    <!-- Mensaje si no hay sucursales -->
    <v-container v-if="!loading && sucursales.length === 0">
      <v-row>
        <v-col cols="12" class="text-center py-12">
          <v-icon x-large color="grey lighten-1" class="mb-4">mdi-store-off</v-icon>
          <p class="text-h5 grey--text">No hay sucursales disponibles</p>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import ListaTiendasMobile from '../components/mobile/ListaTiendasMobile.vue'
import GoBack from '../components/GoBack.vue'
import { mapActions } from 'vuex'
import { Plugins } from '@capacitor/core'
import FranquiciaHero from '../components/FranquiciaHero.vue'
import BuscadorSucursales from '../components/BuscadorSucursales.vue'
import SucursalCard from '../components/cards/SucursalCard.vue'

const { Geolocation } = Plugins

export default {
  components: {
    ListaTiendasMobile,
    GoBack,
    FranquiciaHero,
    BuscadorSucursales,
    SucursalCard
  },
  name: 'SucursalesFranquicia',
  props: {
    id: {
      type: String
    }
  },
  methods: {
    ...mapActions('App', [
      'getSucursalesFranquicia',
      'setAppCustomerPosition',
      'setAppFranquiciaSeleccionada',
      'setFranchiseLogo',
      'setFranchiseName'
    ]),
    async getCustomerPosition (cords) {
      // Manejar tanto la estructura de geolocation API como Capacitor
      const latitude = cords.coords?.latitude || cords.latitude
      const longitude = cords.coords?.longitude || cords.longitude

      console.log('Coordenadas obtenidas:', { latitude, longitude, cords })

      if (!latitude || !longitude || isNaN(latitude) || isNaN(longitude)) {
        console.error('Coordenadas inválidas recibidas:', { latitude, longitude })
        await this.getSucursales()
        return
      }

      this.setAppCustomerPosition({
        latitude: parseFloat(latitude),
        longitude: parseFloat(longitude)
      })

      // Verificar que se guardó correctamente
      const savedPosition = this.$store.state.App.customerPosition
      console.log('Posición guardada en store:', savedPosition)

      await this.getSucursales()
    },
    async handlerGeopositionErros (erros) {
      if (erros.code === 1) {
        this.loading = false
        this.accesoDenegadoUsuario = true
      }
      await this.getSucursales()
    },
    getPermissions () {
      if (navigator.geolocation) {
        const locationTimeout = setTimeout(() => {
          this.geolocFail()
        }, 10000)

        navigator.geolocation.getCurrentPosition(
          (position) => {
            clearTimeout(locationTimeout)
            this.getCustomerPosition(position)
          },
          (error) => {
            clearTimeout(locationTimeout)
            this.handlerGeopositionErros(error)
          }
        )
      } else {
        Geolocation.getCurrentPosition()
          .then(coordinates => this.getCustomerPosition(coordinates))
          .catch(() => this.geolocFail())
      }
    },
    geolocFail () {
      this.accesoDenegadoUsuario = false
      this.getSucursales()
    },
    async getSucursales () {
      try {
        console.log('Obteniendo sucursales para franquicia:', this.id)
        const customerPosition = this.$store.state.App.customerPosition
        console.log('Posición del cliente:', customerPosition)

        this.sucursales = await this.getSucursalesFranquicia(this.id)

        // Validar que sucursales sea un array
        if (!Array.isArray(this.sucursales)) {
          console.error('Error: getSucursalesFranquicia no devolvió un array:', this.sucursales)
          this.sucursales = []
          return
        }

        console.log('Sucursales recibidas del API:', this.sucursales.length)

        if (this.sucursales.length > 0) {
          const primera = this.sucursales[0]
          this.datosFranquicia = {
            Img: primera.Img,
            PrimaryColor: primera.PrimaryColor || '#1976D2',
            SecondaryColor: primera.SecondaryColor || '#424242',
            NombreFranquicia: primera.NombreFranquicia,
            ImagenHeader: primera.ImagenHeader || null,
            TituloHeader: primera.TituloHeader || null,
            SubtituloHeader: primera.SubtituloHeader || null
          }

          await this.setFranchiseLogo(this.datosFranquicia.Img)
          await this.setFranchiseName(this.datosFranquicia.NombreFranquicia)
          this.$vuetify.theme.themes.light.primary = this.datosFranquicia.PrimaryColor
          this.$vuetify.theme.themes.light.secondary = this.datosFranquicia.SecondaryColor

          // Identificar sucursal más cercana
          this.identificarSucursalMasCercana()

          // Inicializar filtros
          this.filtrarSucursales()

          // Log para depuración
          console.log('Sucursales cargadas:', this.sucursales.length)
          console.log('Sucursales filtradas:', this.sucursalesFiltradas.length)
          if (this.sucursalMasCercana) {
            console.log('Sucursal más cercana:', this.sucursalMasCercana.Code || this.sucursalMasCercana.Name)
          }
        } else {
          console.warn('No se encontraron sucursales para la franquicia:', this.id)
        }
      } catch (error) {
        console.error('Error al cargar sucursales:', error)
        console.error('Detalles del error:', error.response || error.message)
        this.sucursales = []
        this.sucursalesFiltradas = []
      }
    },
    identificarSucursalMasCercana () {
      if (!this.sucursales || this.sucursales.length === 0) {
        this.sucursalMasCercana = null
        return
      }

      console.log('Identificando sucursal más cercana de', this.sucursales.length, 'sucursales')

      // Log de distancias para depuración
      this.sucursales.forEach((s, index) => {
        console.log(`Sucursal ${index}: ${s.Name || s.Nombre} - Distancia: ${s.Distancia} (tipo: ${typeof s.Distancia}, válida: ${this.esDistanciaValida(s.Distancia)})`)
      })

      // Buscar la sucursal marcada como más cercana
      const masCercana = this.sucursales.find(s => s.MasCercana === true || s.MasCercana === 'true')
      if (masCercana && this.esDistanciaValida(masCercana.Distancia)) {
        console.log('Sucursal marcada como más cercana encontrada:', masCercana.Name || masCercana.Nombre)
        this.sucursalMasCercana = masCercana
        return
      }

      // Si no hay marca, buscar por distancia menor
      // La distancia viene en metros desde el backend
      const conDistancia = this.sucursales
        .filter(s => this.esDistanciaValida(s.Distancia))
        .sort((a, b) => {
          const distA = typeof a.Distancia === 'number' ? a.Distancia : parseFloat(a.Distancia) || Infinity
          const distB = typeof b.Distancia === 'number' ? b.Distancia : parseFloat(b.Distancia) || Infinity
          return distA - distB
        })

      console.log('Sucursales con distancia válida:', conDistancia.length)

      if (conDistancia.length > 0) {
        console.log('Sucursal más cercana identificada:', conDistancia[0].Name || conDistancia[0].Nombre, '- Distancia:', conDistancia[0].Distancia, 'metros')
        this.sucursalMasCercana = conDistancia[0]
      } else {
        // Si no hay sucursales con distancia válida, NO establecer ninguna como más cercana
        console.log('No se encontraron sucursales con distancia válida. No se mostrará sucursal destacada.')
        this.sucursalMasCercana = null
      }
    },
    esDistanciaValida (distancia) {
      // La distancia es válida si:
      // - Existe
      // - Es un número mayor a 0
      // - No es -1 (valor que indica que no hay distancia)
      // - No es 0 (que puede indicar que no se pudo calcular)
      if (distancia === null || distancia === undefined) return false
      if (distancia === -1) return false
      if (distancia === '0 Km' || distancia === '0') return false
      if (typeof distancia === 'string' && distancia.trim() === '') return false

      const numDistancia = typeof distancia === 'number' ? distancia : parseFloat(distancia)
      if (isNaN(numDistancia)) return false
      if (numDistancia <= 0) return false

      return true
    },
    aplicarFiltros ({ textoBusqueda }) {
      this.textoBusqueda = textoBusqueda || ''
      this.filtrarSucursales()
    },
    filtrarSucursales () {
      if (!this.sucursales || this.sucursales.length === 0) {
        this.sucursalesFiltradas = []
        this.sucursalesAgrupadas = []
        return
      }

      let filtradas = [...this.sucursales]

      // Excluir la más cercana de la lista normal si existe
      if (this.sucursalMasCercana) {
        // Usar el Code como identificador principal, ya que es único
        const codeMasCercana = this.sucursalMasCercana.Code
        if (codeMasCercana) {
          filtradas = filtradas.filter(s => {
            // Comparar principalmente por Code que es único
            if (s.Code && codeMasCercana && s.Code === codeMasCercana) {
              return false // Excluir esta sucursal
            }
            return true // Mantener esta sucursal
          })
        } else {
          // Si no hay Code, usar comparación por múltiples campos como fallback
          filtradas = filtradas.filter(s => {
            // Comparar por múltiples campos para mayor precisión
            const mismoCode = s.Code && this.sucursalMasCercana.Code && s.Code === this.sucursalMasCercana.Code
            const mismoName = s.Name && this.sucursalMasCercana.Name && s.Name === this.sucursalMasCercana.Name
            const mismoNombre = s.Nombre && this.sucursalMasCercana.Nombre && s.Nombre === this.sucursalMasCercana.Nombre
            const mismaDireccion = s.Direccion && this.sucursalMasCercana.Direccion &&
                                  s.Direccion === this.sucursalMasCercana.Direccion
            const mismoAddress = s.Address && this.sucursalMasCercana.Address &&
                                s.Address === this.sucursalMasCercana.Address
            // Solo excluir si hay una coincidencia clara
            return !(mismoCode || (mismoName && mismaDireccion) || (mismoNombre && mismaDireccion) || (mismoAddress && mismoName))
          })
        }
      }

      // Aplicar filtro de búsqueda de texto
      if (this.textoBusqueda && this.textoBusqueda.trim() !== '') {
        const busqueda = this.textoBusqueda.toLowerCase().trim()
        filtradas = filtradas.filter(s => {
          const nombre = ((s.Name || s.Nombre) || '').toLowerCase()
          const direccion = (s.Direccion || s.Address || '').toLowerCase()
          const ciudad = (s.Ciudad || '').toLowerCase()
          const provincia = (s.Provincia || '').toLowerCase()
          const telefono = (s.Telefono || '').toLowerCase()

          return nombre.includes(busqueda) ||
                 direccion.includes(busqueda) ||
                 ciudad.includes(busqueda) ||
                 provincia.includes(busqueda) ||
                 telefono.includes(busqueda)
        })
      }

      // Agrupar por provincia si no hay filtros
      if (!this.textoBusqueda || this.textoBusqueda.trim() === '') {
        const agrupadas = {}
        filtradas.forEach(s => {
          const prov = s.Provincia || 'Sin provincia'
          if (!agrupadas[prov]) {
            agrupadas[prov] = []
          }
          agrupadas[prov].push(s)
        })

        // Ordenar por provincia
        const provinciasOrdenadas = Object.keys(agrupadas).sort()
        this.sucursalesAgrupadas = provinciasOrdenadas.map(prov => ({
          provincia: prov,
          sucursales: agrupadas[prov]
        }))
      } else {
        this.sucursalesAgrupadas = [{ provincia: null, sucursales: filtradas }]
      }

      this.sucursalesFiltradas = filtradas
    },
    limpiarFiltros () {
      this.textoBusqueda = ''
      this.filtrarSucursales()
    }
  },
  computed: {
    sucursalesParaMostrar () {
      // Si hay sucursales agrupadas, usarlas
      if (this.sucursalesAgrupadas.length > 0) {
        return this.sucursalesAgrupadas.flatMap(grupo => grupo.sucursales)
      }

      // Si no hay agrupadas pero hay filtradas, usar las filtradas
      if (this.sucursalesFiltradas.length > 0) {
        return this.sucursalesFiltradas
      }

      // Como último recurso, usar las sucursales originales (fallback)
      if (this.sucursales && this.sucursales.length > 0) {
        // Excluir la más cercana si existe
        if (this.sucursalMasCercana && this.sucursalMasCercana.Code) {
          return this.sucursales.filter(s => s.Code !== this.sucursalMasCercana.Code)
        }
        return this.sucursales
      }

      return []
    },
    mostrarSoloLista () {
      // Si hay filtros activos, no mostrar la sección destacada
      return this.textoBusqueda && this.textoBusqueda.trim() !== ''
    }
  },
  data () {
    return {
      sucursales: [],
      sucursalesFiltradas: [],
      sucursalesAgrupadas: [],
      datosFranquicia: null,
      sucursalMasCercana: null,
      textoBusqueda: '',
      loading: false,
      previousPage: {
        name: 'Tiendas'
      },
      showGoBack: false,
      accesoDenegadoUsuario: false
    }
  },
  async mounted () {
    await this.setAppFranquiciaSeleccionada(this.id)
    this.loading = true
    this.showGoBack = process.env.VUE_APP_LISTAR_FRANQUICIAS === 'FALSE'
    await this.getPermissions()
    this.loading = false
  }
}
</script>

<style scoped>
.franquicias-view {
  min-height: 100vh;
  background-color: #f5f5f5;
}

.seccion-destacada {
  margin-bottom: 32px;
}

.seccion-busqueda {
  margin-bottom: 24px;
}

.seccion-titulo {
  font-size: 1.5rem;
  font-weight: 600;
  color: #212121;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
}

.destacada-card {
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 960px) {
  .seccion-titulo {
    font-size: 1.25rem;
  }

  .franquicias-view {
    background-color: white;
  }
}
</style>
